/**
 * Company Settings Page - Firmeneinstellungen
 * Optimiert für alle Geräte mit responsivem Design
 */

import React, { useState, useEffect } from "react";
import {
  Container,
  Paper,
  Typography,
  Box,
  Stack,
  Grid,
  TextField,
  Button,
  Alert,
  Card,
  CardContent,
  CardActions,
  Divider,
  useTheme,
  useMediaQuery,
  Switch,
  FormControlLabel,
  MenuItem,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import {
  Business as BusinessIcon,
  Save as SaveIcon,
  Edit as EditIcon,
  Cancel as CancelIcon,
  Store as StoreIcon,
  AccountBalance as AccountBalanceIcon,
  CloudUpload as CloudUploadIcon,
  WhatsApp as WhatsAppIcon,
  EventAvailable as EventAvailableIcon,
} from "@mui/icons-material";
import { apiClient } from "../../services/apiClient";
import { getAuthToken } from "../../services/apiClient";
import { getApiBaseUrl } from "../../utils/environment";
import {
  ChatReservation,
  ChatVisit,
  Company,
  CreateChatReservationRequest,
  CreateChatVisitRequest,
  ObjectType,
  ReservationPolicy,
} from "../../types/api-types";
import {
  BusinessStatusInfo,
  SetBusinessStatusRequest,
} from "../../types/api-types";
import LoadingSpinner from "../../components/Common/LoadingSpinner";

const CompanySettingsPage: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [company, setCompany] = useState<Company | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [logoUploading, setLogoUploading] = useState(false);
  const [whatsappSyncing, setWhatsappSyncing] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [logoPreviewUrl, setLogoPreviewUrl] = useState<string | null>(null);

  // Business Status State
  const [businessStatus, setBusinessStatus] =
    useState<BusinessStatusInfo | null>(null);
  const [businessStatusLoading, setBusinessStatusLoading] = useState(false);
  const [businessStatusSaving, setBusinessStatusSaving] = useState(false);

  const [formData, setFormData] = useState({
    name: "",
    legalForm: "",
    street: "",
    zipCode: "",
    city: "",
    country: "Deutschland",
    phone: "",
    email: "",
    website: "",
    taxNumber: "",
    vatNumber: "",
    isSmallBusiness: false,
    defaultVatRate: 19.0,
    smallBusinessNote: "Gemäß § 19 UStG wird keine Umsatzsteuer berechnet.",
    bankName: "",
    iban: "",
    bic: "",
    invoiceFooter: "",
    reservationsEnabled: false,
    reservableHebebuehnePercent: 0,
    reservableArbeitsplatzPercent: 0,
  });

  const [reservationPolicy, setReservationPolicy] =
    useState<ReservationPolicy | null>(null);
  const [reservations, setReservations] = useState<ChatReservation[]>([]);
  const [visits, setVisits] = useState<ChatVisit[]>([]);
  const [reservationLoading, setReservationLoading] = useState(false);
  const [reservationSaving, setReservationSaving] = useState(false);
  const [visitSaving, setVisitSaving] = useState(false);
  const [reservationForm, setReservationForm] =
    useState<CreateChatReservationRequest>({
      customerName: "",
      customerPhone: "",
      objectType: ObjectType.HEBEBUEHNE,
      reservationDate: new Date().toISOString().slice(0, 10),
      startTime: "09:00",
      estimatedHours: 2,
      workDescription: "",
      notes: "",
    });
  const [visitForm, setVisitForm] = useState<CreateChatVisitRequest>({
    customerName: "",
    customerPhone: "",
    visitDate: new Date().toISOString().slice(0, 10),
    startTime: "09:00",
    estimatedHours: 2,
    workDescription: "",
    notes: "",
  });

  const apiBaseUrl = getApiBaseUrl();

  // Business Status Form Data
  const [businessFormData, setBusinessFormData] =
    useState<SetBusinessStatusRequest>({
      isOpen: true,
      reason: "",
      statusMessage: "",
    });

  // Load company data
  const loadCompany = async () => {
    try {
      setLoading(true);
      setError(null);

      const companies = await apiClient.getCompanies();
      const primaryCompany =
        companies.find((c) => c.isPrimary) || companies[0] || null;

      if (primaryCompany) {
        setCompany(primaryCompany);
        setFormData({
          name: primaryCompany.name || "",
          legalForm: primaryCompany.legalForm || "",
          street: primaryCompany.street || "",
          zipCode: primaryCompany.zipCode || "",
          city: primaryCompany.city || "",
          country: primaryCompany.country || "Deutschland",
          phone: primaryCompany.phone || "",
          email: primaryCompany.email || "",
          website: primaryCompany.website || "",
          taxNumber: primaryCompany.taxNumber || "",
          vatNumber: primaryCompany.vatNumber || "",
          isSmallBusiness: primaryCompany.isSmallBusiness || false,
          defaultVatRate: primaryCompany.defaultVatRate || 19.0,
          smallBusinessNote:
            primaryCompany.smallBusinessNote ||
            "Gemäß § 19 UStG wird keine Umsatzsteuer berechnet.",
          bankName: primaryCompany.bankName || "",
          iban: primaryCompany.iban || "",
          bic: primaryCompany.bic || "",
          invoiceFooter: primaryCompany.invoiceFooter || "",
          reservationsEnabled: Boolean(primaryCompany.reservationsEnabled),
          reservableHebebuehnePercent:
            primaryCompany.reservableHebebuehnePercent || 0,
          reservableArbeitsplatzPercent:
            primaryCompany.reservableArbeitsplatzPercent || 0,
        });
      }
    } catch (err: any) {
      setError(err.message || "Fehler beim Laden der Firmendaten");
    } finally {
      setLoading(false);
    }
  };

  // Load business status
  const loadBusinessStatus = async () => {
    try {
      setBusinessStatusLoading(true);
      const status = await apiClient.getBusinessStatus();
      setBusinessStatus(status);
      setBusinessFormData({
        isOpen: status.isOpen,
        reason: status.reason || "",
        statusMessage: status.statusMessage || "",
      });
    } catch (err: any) {
      console.error("Fehler beim Laden des Business-Status:", err);
    } finally {
      setBusinessStatusLoading(false);
    }
  };

  const loadReservations = async () => {
    try {
      setReservationLoading(true);
      const [policy, reservationList, visitList] = await Promise.all([
        apiClient.getReservationPolicy(),
        apiClient.getChatReservations(),
        apiClient.getChatVisits(),
      ]);
      setReservationPolicy(policy);
      setReservations(reservationList);
      setVisits(visitList);
    } catch (err: any) {
      console.error("Fehler beim Laden der Reservierungen:", err);
      setError(err.message || "Reservierungen konnten nicht geladen werden");
    } finally {
      setReservationLoading(false);
    }
  };

  useEffect(() => {
    loadCompany();
    loadBusinessStatus();
    loadReservations();
  }, []);

  useEffect(() => {
    let revokedUrl: string | null = null;
    setLogoPreviewUrl(null);
    if (!company?.logoPath) return undefined;

    void fetch(`${apiBaseUrl.replace(/\/api$/, "")}${company.logoPath}`, {
      headers: { Authorization: `Bearer ${getAuthToken()}` },
    })
      .then((response) => {
        if (!response.ok) throw new Error("Logo konnte nicht geladen werden");
        return response.blob();
      })
      .then((blob) => {
        revokedUrl = URL.createObjectURL(blob);
        setLogoPreviewUrl(revokedUrl);
      })
      .catch(() => setLogoPreviewUrl(null));

    return () => {
      if (revokedUrl) URL.revokeObjectURL(revokedUrl);
    };
  }, [apiBaseUrl, company?.logoPath]);

  const handleSave = async () => {
    setNotice(null);
    if (!company) {
      // Create new company
      try {
        setSaving(true);
        await apiClient.createCompany({
          ...formData,
          reservableHebebuehnePercent: Math.min(
            100,
            Math.max(0, formData.reservableHebebuehnePercent)
          ),
          reservableArbeitsplatzPercent: Math.min(
            100,
            Math.max(0, formData.reservableArbeitsplatzPercent)
          ),
          isPrimary: true,
          isActive: true,
        });
        await loadCompany();
        await loadReservations();
        setIsEditing(false);
      } catch (err: any) {
        setError(err.message || "Fehler beim Erstellen der Firma");
      } finally {
        setSaving(false);
      }
    } else {
      // Update existing company
      try {
        setSaving(true);
        await apiClient.updateCompany(company.id, {
          ...formData,
          reservableHebebuehnePercent: Math.min(
            100,
            Math.max(0, formData.reservableHebebuehnePercent)
          ),
          reservableArbeitsplatzPercent: Math.min(
            100,
            Math.max(0, formData.reservableArbeitsplatzPercent)
          ),
        });
        await loadCompany();
        await loadReservations();
        setIsEditing(false);
      } catch (err: any) {
        setError(err.message || "Fehler beim Speichern der Firma");
      } finally {
        setSaving(false);
      }
    }
  };

  const handleLogoUpload = async (file?: File) => {
    if (!company || !file) return;
    setError(null);
    setNotice(null);
    setLogoUploading(true);
    try {
      const formData = new FormData();
      formData.append("logo", file);
      const response = await fetch(`${apiBaseUrl}/companies/${encodeURIComponent(company.id)}/logo/upload`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${getAuthToken()}`,
        },
        body: formData,
      });
      const body = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(body.error || "Logo konnte nicht hochgeladen werden");
      }
      await loadCompany();
      setNotice("Logo wurde gespeichert.");
    } catch (err: any) {
      setError(err.message || "Logo konnte nicht hochgeladen werden");
    } finally {
      setLogoUploading(false);
    }
  };

  const syncWhatsappProfile = async () => {
    if (!company) return;
    setError(null);
    setNotice(null);
    setWhatsappSyncing(true);
    try {
      const response = await fetch(`${apiBaseUrl}/companies/${encodeURIComponent(company.id)}/whatsapp-profile/sync`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getAuthToken()}`,
        },
        body: JSON.stringify({ tenantId: "hobbygarage-frankenthal" }),
      });
      const body = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(body.error || "WhatsApp-Profil konnte nicht synchronisiert werden");
      }
      setNotice("WhatsApp-Profil wurde mit Unternehmensdaten synchronisiert.");
    } catch (err: any) {
      setError(err.message || "WhatsApp-Profil konnte nicht synchronisiert werden");
    } finally {
      setWhatsappSyncing(false);
    }
  };

  const handleBusinessStatusSave = async () => {
    try {
      setBusinessStatusSaving(true);
      await apiClient.setBusinessStatus(businessFormData);
      await loadBusinessStatus();
    } catch (err: any) {
      setError(err.message || "Fehler beim Speichern des Business-Status");
    } finally {
      setBusinessStatusSaving(false);
    }
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleBusinessInputChange = (field: string, value: any) => {
    setBusinessFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleReservationInputChange = (
    field: keyof CreateChatReservationRequest,
    value: any
  ) => {
    setReservationForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const createReservation = async () => {
    try {
      setError(null);
      setNotice(null);
      setReservationSaving(true);
      await apiClient.createChatReservation({
        ...reservationForm,
        estimatedHours: Number(reservationForm.estimatedHours),
      });
      setReservationForm((prev) => ({
        ...prev,
        customerName: "",
        customerPhone: "",
        workDescription: "",
        notes: "",
      }));
      await loadReservations();
      setNotice("Reservierung wurde gespeichert.");
    } catch (err: any) {
      setError(err.message || "Reservierung konnte nicht gespeichert werden");
    } finally {
      setReservationSaving(false);
    }
  };

  const cancelReservation = async (id: string) => {
    try {
      setError(null);
      setNotice(null);
      await apiClient.cancelChatReservation(id);
      await loadReservations();
      setNotice("Reservierung wurde storniert.");
    } catch (err: any) {
      setError(err.message || "Reservierung konnte nicht storniert werden");
    }
  };

  const handleVisitInputChange = (
    field: keyof CreateChatVisitRequest,
    value: any
  ) => {
    setVisitForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const createVisit = async () => {
    try {
      setError(null);
      setNotice(null);
      setVisitSaving(true);
      await apiClient.createChatVisit({
        ...visitForm,
        estimatedHours:
          visitForm.estimatedHours == null
            ? undefined
            : Number(visitForm.estimatedHours),
      });
      setVisitForm((prev) => ({
        ...prev,
        customerName: "",
        customerPhone: "",
        workDescription: "",
        notes: "",
      }));
      await loadReservations();
      setNotice("Besuch wurde eingetragen.");
    } catch (err: any) {
      setError(err.message || "Besuch konnte nicht eingetragen werden");
    } finally {
      setVisitSaving(false);
    }
  };

  const cancelVisit = async (id: string) => {
    try {
      setError(null);
      setNotice(null);
      await apiClient.cancelChatVisit(id);
      await loadReservations();
      setNotice("Besuch wurde storniert.");
    } catch (err: any) {
      setError(err.message || "Besuch konnte nicht storniert werden");
    }
  };

  const objectTypeLabel = (objectType: ObjectType) =>
    objectType === ObjectType.HEBEBUEHNE ? "Hebebühne" : "Arbeitsplatz";

  if (loading) {
    return <LoadingSpinner text="Lade Firmendaten..." />;
  }

  return (
    <Container
      maxWidth="xl"
      sx={{
        py: { xs: 1, md: 3 },
        px: { xs: 0.5, md: 3 },
        maxWidth: { xs: "100%", md: "xl" },
        overflow: "hidden",
      }}
    >
      {/* Header */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 1.5, md: 3 },
          mb: 3,
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          color: "white",
          borderRadius: 2,
        }}
      >
        <Stack
          direction={{ xs: "column", sm: "row" }}
          alignItems={{ xs: "flex-start", sm: "center" }}
          justifyContent="space-between"
          spacing={2}
        >
          <Box>
            <Typography
              variant={isMobile ? "h5" : "h4"}
              component="h1"
              gutterBottom
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                fontWeight: "bold",
                mb: 1,
              }}
            >
              <BusinessIcon fontSize={isMobile ? "medium" : "large"} />
              Firmeneinstellungen
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>
              Verwalten Sie die grundlegenden Informationen Ihres Unternehmens
            </Typography>
          </Box>

          {!isEditing && (
            <Button
              variant="contained"
              startIcon={<EditIcon />}
              onClick={() => setIsEditing(true)}
              sx={{
                bgcolor: "rgba(255,255,255,0.2)",
                "&:hover": { bgcolor: "rgba(255,255,255,0.3)" },
                borderRadius: 2,
              }}
            >
              Bearbeiten
            </Button>
          )}
        </Stack>
      </Paper>

      {/* Error Alert */}
      {error && (
        <Alert
          severity="error"
          sx={{ mb: 3, borderRadius: 2 }}
          onClose={() => setError(null)}
        >
          {error}
        </Alert>
      )}
      {notice && (
        <Alert
          severity="success"
          sx={{ mb: 3, borderRadius: 2 }}
          onClose={() => setNotice(null)}
        >
          {notice}
        </Alert>
      )}

      {/* Main Content */}
      {!company && !isEditing ? (
        <Paper
          sx={{
            p: { xs: 3, md: 6 },
            textAlign: "center",
            borderRadius: 2,
            background: "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)",
          }}
        >
          <BusinessIcon
            sx={{
              fontSize: { xs: 60, md: 80 },
              color: "text.secondary",
              mb: 2,
            }}
          />
          <Typography
            variant={isMobile ? "h6" : "h5"}
            gutterBottom
            fontWeight="bold"
          >
            Keine Firma konfiguriert
          </Typography>
          <Typography variant="body1" color="text.secondary" gutterBottom>
            Richten Sie Ihre Hauptfirma ein, um das System zu verwenden.
          </Typography>
          <Button
            variant="contained"
            size="large"
            startIcon={<BusinessIcon />}
            onClick={() => setIsEditing(true)}
            sx={{
              mt: 3,
              borderRadius: 2,
              px: 4,
              py: 1.5,
            }}
          >
            Firma einrichten
          </Button>
        </Paper>
      ) : (
        <Grid container spacing={3}>
          {/* Company Information Card */}
          <Grid item xs={12} lg={8}>
            <Card
              sx={{ borderRadius: 2, boxShadow: "0 4px 20px rgba(0,0,0,0.1)" }}
            >
              <CardContent sx={{ p: { xs: 2, md: 3 } }}>
                <Typography
                  variant="h6"
                  gutterBottom
                  fontWeight="bold"
                  color="primary"
                >
                  Unternehmensdaten
                </Typography>
                <Divider sx={{ mb: 3 }} />

                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={2}
                  alignItems={{ xs: "stretch", sm: "center" }}
                  sx={{ mb: 3 }}
                >
                  <Box
                    sx={{
                      width: 96,
                      height: 96,
                      borderRadius: 2,
                      border: "1px solid",
                      borderColor: "divider",
                      bgcolor: "grey.50",
                      display: "grid",
                      placeItems: "center",
                      overflow: "hidden",
                    }}
                  >
                    {logoPreviewUrl ? (
                      <Box
                        component="img"
                        src={logoPreviewUrl}
                        alt={`${company.name} Logo`}
                        sx={{ width: "100%", height: "100%", objectFit: "contain" }}
                      />
                    ) : (
                      <BusinessIcon color="disabled" sx={{ fontSize: 42 }} />
                    )}
                  </Box>
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={1} flexWrap="wrap" useFlexGap>
                    <Button
                      component="label"
                      variant="outlined"
                      startIcon={<CloudUploadIcon />}
                      disabled={!company || logoUploading}
                    >
                      {logoUploading ? "Lade hoch..." : "Logo hochladen"}
                      <input
                        hidden
                        type="file"
                        accept="image/png,image/jpeg,image/webp"
                        onChange={(event) => {
                          void handleLogoUpload(event.target.files?.[0]);
                          event.target.value = "";
                        }}
                      />
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<WhatsAppIcon />}
                      onClick={syncWhatsappProfile}
                      disabled={!company || whatsappSyncing}
                    >
                      {whatsappSyncing ? "Synchronisiere..." : "WhatsApp-Profil synchronisieren"}
                    </Button>
                  </Stack>
                </Stack>

                {isEditing ? (
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Firmenname"
                        value={formData.name}
                        onChange={(e) =>
                          handleInputChange("name", e.target.value)
                        }
                        required
                        variant="outlined"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Rechtsform"
                        value={formData.legalForm}
                        onChange={(e) =>
                          handleInputChange("legalForm", e.target.value)
                        }
                        variant="outlined"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Straße und Hausnummer"
                        value={formData.street}
                        onChange={(e) =>
                          handleInputChange("street", e.target.value)
                        }
                        required
                        variant="outlined"
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        fullWidth
                        label="PLZ"
                        value={formData.zipCode}
                        onChange={(e) =>
                          handleInputChange("zipCode", e.target.value)
                        }
                        required
                        variant="outlined"
                      />
                    </Grid>
                    <Grid item xs={12} sm={8}>
                      <TextField
                        fullWidth
                        label="Ort"
                        value={formData.city}
                        onChange={(e) =>
                          handleInputChange("city", e.target.value)
                        }
                        required
                        variant="outlined"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Telefon"
                        value={formData.phone}
                        onChange={(e) =>
                          handleInputChange("phone", e.target.value)
                        }
                        variant="outlined"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="E-Mail"
                        type="email"
                        value={formData.email}
                        onChange={(e) =>
                          handleInputChange("email", e.target.value)
                        }
                        required
                        variant="outlined"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Website"
                        type="url"
                        value={formData.website}
                        onChange={(e) =>
                          handleInputChange("website", e.target.value)
                        }
                        variant="outlined"
                      />
                    </Grid>
                  </Grid>
                ) : (
                  <Box>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary">
                          Firmenname
                        </Typography>
                        <Typography variant="body1" fontWeight="medium">
                          {company?.name || "Nicht angegeben"}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary">
                          Rechtsform
                        </Typography>
                        <Typography variant="body1" fontWeight="medium">
                          {company?.legalForm || "Nicht angegeben"}
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="body2" color="text.secondary">
                          Adresse
                        </Typography>
                        <Typography variant="body1" fontWeight="medium">
                          {company?.street && `${company.street}, `}
                          {company?.zipCode} {company?.city}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary">
                          Telefon
                        </Typography>
                        <Typography variant="body1" fontWeight="medium">
                          {company?.phone || "Nicht angegeben"}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary">
                          E-Mail
                        </Typography>
                        <Typography variant="body1" fontWeight="medium">
                          {company?.email || "Nicht angegeben"}
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="body2" color="text.secondary">
                          Website
                        </Typography>
                        <Typography variant="body1" fontWeight="medium">
                          {company?.website || "Nicht angegeben"}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Tax Information Card */}
          <Grid item xs={12} lg={4}>
            <Card
              sx={{ borderRadius: 2, boxShadow: "0 4px 20px rgba(0,0,0,0.1)" }}
            >
              <CardContent sx={{ p: { xs: 2, md: 3 } }}>
                <Typography
                  variant="h6"
                  gutterBottom
                  fontWeight="bold"
                  color="primary"
                >
                  Steuerinformationen
                </Typography>
                <Divider sx={{ mb: 3 }} />

                {isEditing ? (
                  <Stack spacing={2}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={formData.isSmallBusiness}
                          onChange={(e) =>
                            handleInputChange(
                              "isSmallBusiness",
                              e.target.checked
                            )
                          }
                          color="primary"
                        />
                      }
                      label="Kleinunternehmer (§ 19 UStG)"
                    />

                    {!formData.isSmallBusiness && (
                      <>
                        <TextField
                          fullWidth
                          label="Umsatzsteuer-ID"
                          value={formData.vatNumber}
                          onChange={(e) =>
                            handleInputChange("vatNumber", e.target.value)
                          }
                          variant="outlined"
                        />
                        <TextField
                          fullWidth
                          label="Standard MwSt-Satz (%)"
                          type="number"
                          value={formData.defaultVatRate}
                          onChange={(e) =>
                            handleInputChange(
                              "defaultVatRate",
                              parseFloat(e.target.value) || 19.0
                            )
                          }
                          variant="outlined"
                          inputProps={{ min: 0, max: 100, step: 0.1 }}
                        />
                      </>
                    )}

                    {formData.isSmallBusiness && (
                      <TextField
                        fullWidth
                        label="Hinweis Kleinunternehmer"
                        value={formData.smallBusinessNote}
                        onChange={(e) =>
                          handleInputChange("smallBusinessNote", e.target.value)
                        }
                        variant="outlined"
                        multiline
                        rows={2}
                      />
                    )}

                    <TextField
                      fullWidth
                      label="Steuernummer"
                      value={formData.taxNumber}
                      onChange={(e) =>
                        handleInputChange("taxNumber", e.target.value)
                      }
                      variant="outlined"
                    />
                  </Stack>
                ) : (
                  <Stack spacing={2}>
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Unternehmensart
                      </Typography>
                      <Typography variant="body1" fontWeight="medium">
                        {company?.isSmallBusiness
                          ? "Kleinunternehmer (§ 19 UStG)"
                          : "Regelbesteuerung"}
                      </Typography>
                    </Box>

                    {!company?.isSmallBusiness && (
                      <>
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            Umsatzsteuer-ID
                          </Typography>
                          <Typography variant="body1" fontWeight="medium">
                            {company?.vatNumber || "Nicht angegeben"}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            Standard MwSt-Satz
                          </Typography>
                          <Typography variant="body1" fontWeight="medium">
                            {company?.defaultVatRate || 19.0}%
                          </Typography>
                        </Box>
                      </>
                    )}

                    {company?.isSmallBusiness && (
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          Hinweis Kleinunternehmer
                        </Typography>
                        <Typography variant="body1" fontWeight="medium">
                          {company?.smallBusinessNote || "Nicht angegeben"}
                        </Typography>
                      </Box>
                    )}

                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Steuernummer
                      </Typography>
                      <Typography variant="body1" fontWeight="medium">
                        {company?.taxNumber || "Nicht angegeben"}
                      </Typography>
                    </Box>
                  </Stack>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Bank Information Card */}
          <Grid item xs={12}>
            <Card
              sx={{ borderRadius: 2, boxShadow: "0 4px 20px rgba(0,0,0,0.1)" }}
            >
              <CardContent sx={{ p: { xs: 2, md: 3 } }}>
                <Typography
                  variant="h6"
                  gutterBottom
                  fontWeight="bold"
                  color="primary"
                >
                  Bankverbindung
                </Typography>
                <Divider sx={{ mb: 3 }} />

                {isEditing ? (
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Bankname"
                        value={formData.bankName}
                        onChange={(e) =>
                          handleInputChange("bankName", e.target.value)
                        }
                        variant="outlined"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="BIC"
                        value={formData.bic}
                        onChange={(e) =>
                          handleInputChange("bic", e.target.value)
                        }
                        variant="outlined"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="IBAN"
                        value={formData.iban}
                        onChange={(e) =>
                          handleInputChange("iban", e.target.value)
                        }
                        variant="outlined"
                      />
                    </Grid>
                  </Grid>
                ) : (
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">
                        Bankname
                      </Typography>
                      <Typography variant="body1" fontWeight="medium">
                        {company?.bankName || "Nicht angegeben"}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">
                        BIC
                      </Typography>
                      <Typography variant="body1" fontWeight="medium">
                        {company?.bic || "Nicht angegeben"}
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="body2" color="text.secondary">
                        IBAN
                      </Typography>
                      <Typography variant="body1" fontWeight="medium">
                        {company?.iban || "Nicht angegeben"}
                      </Typography>
                    </Grid>
                  </Grid>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Reservation Settings Card */}
          <Grid item xs={12}>
            <Card
              sx={{ borderRadius: 2, boxShadow: "0 4px 20px rgba(0,0,0,0.1)" }}
            >
              <CardContent sx={{ p: { xs: 2, md: 3 } }}>
                <Stack
                  direction={{ xs: "column", md: "row" }}
                  justifyContent="space-between"
                  alignItems={{ xs: "flex-start", md: "center" }}
                  spacing={1}
                  sx={{ mb: 2 }}
                >
                  <Typography
                    variant="h6"
                    fontWeight="bold"
                    color="primary"
                    sx={{ display: "flex", alignItems: "center", gap: 1 }}
                  >
                    <EventAvailableIcon />
                    Reservierungen
                  </Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    <Chip
                      color={
                        reservationPolicy?.reservationsEnabled
                          ? "success"
                          : "default"
                      }
                      label={
                        reservationPolicy?.reservationsEnabled
                          ? "Reservierungen aktiv"
                          : "Reservierungen aus"
                      }
                    />
                    <Chip
                      variant="outlined"
                      label={`Hebebühnen: ${
                        reservationPolicy?.capacity?.HEBEBUEHNE.reservable ?? 0
                      }/${
                        reservationPolicy?.capacity?.HEBEBUEHNE.total ?? 0
                      } reservierbar`}
                    />
                    <Chip
                      variant="outlined"
                      label={`Arbeitsplätze: ${
                        reservationPolicy?.capacity?.ARBEITSPLATZ.reservable ??
                        0
                      }/${
                        reservationPolicy?.capacity?.ARBEITSPLATZ.total ?? 0
                      } reservierbar`}
                    />
                  </Stack>
                </Stack>
                <Divider sx={{ mb: 3 }} />

                {isEditing ? (
                  <Grid container spacing={2} sx={{ mb: 3 }}>
                    <Grid item xs={12} md={4}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={formData.reservationsEnabled}
                            onChange={(e) =>
                              handleInputChange(
                                "reservationsEnabled",
                                e.target.checked
                              )
                            }
                            color="primary"
                          />
                        }
                        label="Kundenreservierungen erlauben"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <TextField
                        fullWidth
                        label="Reservierbare Hebebühnen (%)"
                        type="number"
                        value={formData.reservableHebebuehnePercent}
                        onChange={(e) =>
                          handleInputChange(
                            "reservableHebebuehnePercent",
                            Number(e.target.value)
                          )
                        }
                        inputProps={{ min: 0, max: 100, step: 1 }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <TextField
                        fullWidth
                        label="Reservierbare Arbeitsplätze (%)"
                        type="number"
                        value={formData.reservableArbeitsplatzPercent}
                        onChange={(e) =>
                          handleInputChange(
                            "reservableArbeitsplatzPercent",
                            Number(e.target.value)
                          )
                        }
                        inputProps={{ min: 0, max: 100, step: 1 }}
                      />
                    </Grid>
                  </Grid>
                ) : (
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 3 }}>
                    <Chip
                      label={`Hebebühnen-Quote: ${
                        company?.reservableHebebuehnePercent || 0
                      }%`}
                    />
                    <Chip
                      label={`Arbeitsplatz-Quote: ${
                        company?.reservableArbeitsplatzPercent || 0
                      }%`}
                    />
                  </Stack>
                )}

                <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>
                  Reservierung anlegen
                </Typography>
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  <Grid item xs={12} md={3}>
                    <TextField
                      fullWidth
                      label="Name"
                      value={reservationForm.customerName}
                      onChange={(e) =>
                        handleReservationInputChange(
                          "customerName",
                          e.target.value
                        )
                      }
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      fullWidth
                      label="Telefonnummer"
                      value={reservationForm.customerPhone}
                      onChange={(e) =>
                        handleReservationInputChange(
                          "customerPhone",
                          e.target.value
                        )
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={2}>
                    <TextField
                      fullWidth
                      select
                      label="Objektart"
                      value={reservationForm.objectType}
                      onChange={(e) =>
                        handleReservationInputChange(
                          "objectType",
                          e.target.value
                        )
                      }
                    >
                      <MenuItem value={ObjectType.HEBEBUEHNE}>
                        Hebebühne
                      </MenuItem>
                      <MenuItem value={ObjectType.ARBEITSPLATZ}>
                        Arbeitsplatz
                      </MenuItem>
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={6} md={2}>
                    <TextField
                      fullWidth
                      label="Tag"
                      type="date"
                      value={reservationForm.reservationDate}
                      onChange={(e) =>
                        handleReservationInputChange(
                          "reservationDate",
                          e.target.value
                        )
                      }
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={6} sm={3} md={1}>
                    <TextField
                      fullWidth
                      label="Uhrzeit"
                      type="time"
                      value={reservationForm.startTime}
                      onChange={(e) =>
                        handleReservationInputChange("startTime", e.target.value)
                      }
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={6} sm={3} md={1}>
                    <TextField
                      fullWidth
                      label="Std."
                      type="number"
                      value={reservationForm.estimatedHours}
                      onChange={(e) =>
                        handleReservationInputChange(
                          "estimatedHours",
                          Number(e.target.value)
                        )
                      }
                      inputProps={{ min: 0.25, step: 0.25 }}
                    />
                  </Grid>
                  <Grid item xs={12} md={9}>
                    <TextField
                      fullWidth
                      label="Was soll gemacht werden?"
                      value={reservationForm.workDescription}
                      onChange={(e) =>
                        handleReservationInputChange(
                          "workDescription",
                          e.target.value
                        )
                      }
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Button
                      fullWidth
                      variant="contained"
                      startIcon={<EventAvailableIcon />}
                      onClick={createReservation}
                      disabled={
                        reservationSaving ||
                        reservationLoading ||
                        !reservationPolicy?.reservationsEnabled ||
                        !reservationForm.customerName ||
                        !reservationForm.customerPhone ||
                        !reservationForm.workDescription
                      }
                      sx={{ height: "56px" }}
                    >
                      {reservationSaving ? "Speichere..." : "Reservieren"}
                    </Button>
                  </Grid>
                </Grid>

                <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>
                  Übersicht
                </Typography>
                <TableContainer
                  component={Paper}
                  variant="outlined"
                  sx={{ maxHeight: 420 }}
                >
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>Tag</TableCell>
                        <TableCell>Uhrzeit</TableCell>
                        <TableCell>Objektart</TableCell>
                        <TableCell>Kunde</TableCell>
                        <TableCell>Telefon</TableCell>
                        <TableCell>Arbeit</TableCell>
                        <TableCell>Dauer</TableCell>
                        <TableCell align="right">Aktion</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {reservations.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={8} align="center">
                            {reservationLoading
                              ? "Lade Reservierungen..."
                              : "Keine Reservierungen vorhanden"}
                          </TableCell>
                        </TableRow>
                      ) : (
                        reservations.map((reservation) => (
                          <TableRow key={reservation.id} hover>
                            <TableCell>{reservation.reservationDate}</TableCell>
                            <TableCell>{reservation.startTime}</TableCell>
                            <TableCell>
                              {objectTypeLabel(reservation.objectType)}
                            </TableCell>
                            <TableCell>{reservation.customerName}</TableCell>
                            <TableCell>{reservation.customerPhone}</TableCell>
                            <TableCell>{reservation.workDescription}</TableCell>
                            <TableCell>
                              {Number(reservation.estimatedHours)} Std.
                            </TableCell>
                            <TableCell align="right">
                              <Button
                                size="small"
                                color="error"
                                onClick={() => cancelReservation(reservation.id)}
                              >
                                Stornieren
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>

                <Divider sx={{ my: 3 }} />
                <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>
                  Besuchskalender
                </Typography>
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  <Grid item xs={12} md={3}>
                    <TextField
                      fullWidth
                      label="Name"
                      value={visitForm.customerName}
                      onChange={(e) =>
                        handleVisitInputChange("customerName", e.target.value)
                      }
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      fullWidth
                      label="Telefonnummer"
                      value={visitForm.customerPhone}
                      onChange={(e) =>
                        handleVisitInputChange("customerPhone", e.target.value)
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={2}>
                    <TextField
                      fullWidth
                      label="Tag"
                      type="date"
                      value={visitForm.visitDate}
                      onChange={(e) =>
                        handleVisitInputChange("visitDate", e.target.value)
                      }
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={6} sm={3} md={1}>
                    <TextField
                      fullWidth
                      label="Uhrzeit"
                      type="time"
                      value={visitForm.startTime}
                      onChange={(e) =>
                        handleVisitInputChange("startTime", e.target.value)
                      }
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={6} sm={3} md={1}>
                    <TextField
                      fullWidth
                      label="Std."
                      type="number"
                      value={visitForm.estimatedHours}
                      onChange={(e) =>
                        handleVisitInputChange(
                          "estimatedHours",
                          Number(e.target.value)
                        )
                      }
                      inputProps={{ min: 0.25, step: 0.25 }}
                    />
                  </Grid>
                  <Grid item xs={12} md={10}>
                    <TextField
                      fullWidth
                      label="Was will der Kunde machen?"
                      value={visitForm.workDescription}
                      onChange={(e) =>
                        handleVisitInputChange(
                          "workDescription",
                          e.target.value
                        )
                      }
                    />
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <Button
                      fullWidth
                      variant="outlined"
                      startIcon={<EventAvailableIcon />}
                      onClick={createVisit}
                      disabled={
                        visitSaving ||
                        reservationLoading ||
                        !visitForm.customerName ||
                        !visitForm.workDescription
                      }
                      sx={{ height: "56px" }}
                    >
                      {visitSaving ? "Speichere..." : "Eintragen"}
                    </Button>
                  </Grid>
                </Grid>

                <TableContainer
                  component={Paper}
                  variant="outlined"
                  sx={{ maxHeight: 420 }}
                >
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>Tag</TableCell>
                        <TableCell>Uhrzeit</TableCell>
                        <TableCell>Kunde</TableCell>
                        <TableCell>Telefon</TableCell>
                        <TableCell>Vorhaben</TableCell>
                        <TableCell>Dauer</TableCell>
                        <TableCell>Quelle</TableCell>
                        <TableCell align="right">Aktion</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {visits.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={8} align="center">
                            {reservationLoading
                              ? "Lade Besuchskalender..."
                              : "Keine Besuche eingetragen"}
                          </TableCell>
                        </TableRow>
                      ) : (
                        visits.map((visit) => (
                          <TableRow key={visit.id} hover>
                            <TableCell>{visit.visitDate}</TableCell>
                            <TableCell>{visit.startTime}</TableCell>
                            <TableCell>{visit.customerName}</TableCell>
                            <TableCell>{visit.customerPhone || "-"}</TableCell>
                            <TableCell>{visit.workDescription}</TableCell>
                            <TableCell>
                              {visit.estimatedHours
                                ? `${Number(visit.estimatedHours)} Std.`
                                : "-"}
                            </TableCell>
                            <TableCell>{visit.source || "manual"}</TableCell>
                            <TableCell align="right">
                              <Button
                                size="small"
                                color="error"
                                onClick={() => cancelVisit(visit.id)}
                              >
                                Stornieren
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Grid>



          {/* Action Buttons */}
          {isEditing && (
            <Grid item xs={12}>
              <Card
                sx={{
                  borderRadius: 2,
                  boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
                }}
              >
                <CardActions
                  sx={{
                    p: { xs: 2, md: 3 },
                    justifyContent: "flex-end",
                    gap: 2,
                  }}
                >
                  <Button
                    variant="outlined"
                    startIcon={<CancelIcon />}
                    onClick={() => {
                      setIsEditing(false);
                      loadCompany(); // Reset form data
                    }}
                    disabled={saving}
                  >
                    Abbrechen
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={<SaveIcon />}
                    onClick={handleSave}
                    disabled={saving}
                  >
                    {saving ? "Speichere..." : "Speichern"}
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          )}
        </Grid>
      )}
    </Container>
  );
};

export default CompanySettingsPage;
